import type { RefObject } from "react";
import type { Survey } from "../survey/schema";
import type { Message } from "../state/chatStore";
import type { Answer } from "../state/persistence";
import type { Phase } from "../state/chatPhase";
import { MessageBubble } from "./MessageBubble";
import { ChipScale } from "./ChipScale";
import { FallbackToggle } from "./FallbackToggle";

/**
 * 会話ログ (spec 2026-08-04 D8)。**`?log=1` のときだけ描画される。**
 *
 * 被験者が使うときは画面に何も出さない — 設問が読めると「音声だけで成立するか」を
 * 検証できないため (spec §1.2)。ここは開発時に会話の流れを目で追うためのもので、
 * ChatScreen から JSX をそのまま移設してある。
 *
 * 会話の中身が失われるわけではない: R2 の events.jsonl (`scribe.final`) と
 * answers.json に残り続け、管理ダッシュボードで後から読める。
 */
type TtsState = "idle" | "playing" | "failed";

type Props = {
  survey: Survey;
  messages: Message[];
  partialText: string;
  /** partial バブルに付ける questionId。定数 (RESUME_PROMPT_QID 等) は呼び出し側が知っている */
  partialQuestionId: string;
  phase: Phase;
  answers: Record<string, Answer>;
  ttsState: Record<string, TtsState>;
  onReplayTts: (messageId: string, text: string) => void;
  onRetry: (questionId: string) => void;
  /** 訂正のロジック (store 更新・R2 保存・計測) は ChatScreen が持つ。ここは入口だけ */
  onEditText: (messageId: string, questionId: string, before: string) => void;
  chipExpanded: boolean;
  onToggleChip: () => void;
  forceChipFallback: boolean;
  onChipPick: (n: number) => void;
  resumeButtonsExpanded: boolean;
  onToggleResumeButtons: () => void;
  forceResumeButtons: boolean;
  onResume: () => void;
  onStartOver: () => void;
  containerRef: RefObject<HTMLDivElement | null>;
  onScroll: () => void;
};

export function ConversationLog({
  survey,
  messages,
  partialText,
  partialQuestionId,
  phase,
  answers,
  ttsState,
  onReplayTts,
  onRetry,
  onEditText,
  chipExpanded,
  onToggleChip,
  forceChipFallback,
  onChipPick,
  resumeButtonsExpanded,
  onToggleResumeButtons,
  forceResumeButtons,
  onResume,
  onStartOver,
  containerRef,
  onScroll,
}: Props) {
  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="thread-scroll flex-1 min-h-0 overflow-y-auto px-3 py-3 flex flex-col gap-2"
    >
      {messages.map((m) => {
        const lastQ =
          m.kind === "ai-question" ? survey.questions.find((q) => q.id === m.questionId) : null;
        const isUser = m.kind === "user-transcript";
        const userQ = isUser ? survey.questions.find((q) => q.id === m.questionId) : null;
        return (
          <MessageBubble
            key={m.id}
            message={m}
            ttsState={isUser ? undefined : (ttsState[m.id] ?? "idle")}
            onReplayTts={isUser ? undefined : () => onReplayTts(m.id, m.text)}
            onRetry={isUser && userQ ? () => onRetry(m.questionId) : undefined}
            onEditText={
              isUser && (userQ?.type === "short" || userQ?.type === "long")
                ? () => onEditText(m.id, m.questionId, m.text)
                : undefined
            }
          >
            {lastQ &&
              lastQ.type === "scale" &&
              m.id === messages[messages.length - 1].id &&
              phase === "asking" && (
                <FallbackToggle
                  label="数字で選ぶ"
                  icon="🔢"
                  expanded={chipExpanded}
                  forceExpand={forceChipFallback}
                  onToggle={onToggleChip}
                >
                  <ChipScale
                    min={lastQ.min}
                    max={lastQ.max}
                    minLabel={lastQ.minLabel}
                    maxLabel={lastQ.maxLabel}
                    value={(answers[lastQ.id]?.value as number) ?? null}
                    onPick={onChipPick}
                    disabled={phase !== "asking"}
                  />
                </FallbackToggle>
              )}
          </MessageBubble>
        );
      })}
      {partialText && (
        // streaming STT (spec §3.12): Scribe の partial_transcript を薄色 (opacity-60) 表示。
        // commit で本物の user-transcript が append されると同時に partialText は "" に戻る。
        <MessageBubble
          key="__partial__"
          message={{
            id: "__partial__",
            kind: "user-transcript",
            questionId: partialQuestionId,
            text: partialText,
            createdAt: 0,
          }}
          partial
        />
      )}
      {phase === "resume-prompt" && (
        <div className="mt-1 px-1">
          <FallbackToggle
            label="ボタンで選ぶ"
            icon="⌨"
            expanded={resumeButtonsExpanded}
            forceExpand={forceResumeButtons}
            onToggle={onToggleResumeButtons}
          >
            {/* min-h-[44px] は Apple HIG のタップ領域 (44x44pt)。gap は 8px → 12px:
                「最初から」は全回答の消去で取り消せないので、隣の当たり判定と離す。 */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onResume}
                className="min-h-[44px] inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
              >
                続きから
              </button>
              <button
                type="button"
                onClick={onStartOver}
                className="min-h-[44px] inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                最初から
              </button>
            </div>
          </FallbackToggle>
        </div>
      )}
      {phase === "processing" && (
        // 回答後の "間" の不安対策 (spec D5): 呼吸するインジケータ + 「少々お待ちください…」で
        // 「システムが働いている、待って大丈夫」を暗に示す。数値カウントダウンは締切感を与える
        // ので使わない (§6.3)。pause 中 / LLM 待ち中の Reason Unknown も防ぐ (§10.2 / §11.1)。
        <div className="flex flex-col items-center gap-2 py-3" role="status">
          <span
            className="inline-block h-3 w-3 rounded-full bg-indigo-400/70 animate-pulse"
            aria-hidden="true"
          />
          <p className="text-center text-slate-500 text-sm">少々お待ちください…</p>
        </div>
      )}
    </div>
  );
}
